// ⚠️⚠️⚠️ Disclaimer: This GDPR template is provided for informational purposes only and is intended as a guideline. The responsibility for the implementation and compliance with GDPR regulations lies solely with the individual or entity using this template. By utilizing this template, you acknowledge that you are solely responsible for ensuring that your application adheres to all applicable data protection laws and regulations. Our organization, which provides this template, shall not be held liable for any damages, losses, or legal consequences arising from the use or misuse of this template. It is imperative that you seek professional legal advice tailored to your specific circumstances before proceeding. Any reliance on this template is at your own risk.

import React from "react";
import { IconExternalLink, IconShield, IconMail, IconCookie } from "@tabler/icons-react";
import { navigate } from "vike/client/router";
import { SpotlightActionData } from "@mantine/spotlight";

async function onSubmit(route: string) {
  const navigationPromise = navigate(route);
  await navigationPromise;
}

export const privacyPolicy: SpotlightActionData[] = [
  {
    id: "gdpr-applicability",
    label: "GDPR Applicability",
    description:
      "The General Data Protection Regulation (GDPR) applies to our processing of personal data as we serve users in the European Union and store data within the EU.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "personal-data-definition",
    label: "What Is Personal Data?",
    description:
      "Personal data includes any information that relates to an identified or identifiable person — such as usernames, avatars (public GitHub profile), and payment information.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "data-processing-contexts",
    label: "When Do We Process Data?",
    description:
      "We process personal data when you: Visit our site, Create an account via GitHub, Make a purchase via Stripe, View or post to public leaderboards, Interact with dashboard features, Send us an email",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "email-purpose",
    label: "Email Data Processing",
    description:
      "Emails are used to respond to user inquiries or service issues. The legal basis is legitimate interest. Your data is stored securely and only for as long as needed.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "email-retention",
    label: "Email Retention Period",
    description:
      "Emails are retained only for the time required to respond to your query, and up to 5 years afterward to meet legal or audit obligations.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "email-transfer",
    label: "Email Storage & Transfers",
    description:
      "Emails are hosted by Mailo, a secure provider based in Paris, France. No email data is transferred outside the EU.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "email-recipients",
    label: "Email Data Recipients",
    description:
      "Email content may be accessed by support staff, legal advisors, or auditors as required for legal compliance or operations.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "email-rights",
    label: "Your Email Rights",
    description:
      "Under GDPR, you have the right to access, rectify, erase, or restrict processing of your email data. You also have rights to object and to decide the fate of your data after death.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "email-cnil",
    label: "CNIL Complaints – Email",
    description: "If you believe your data is misused, you can contact CNIL, France’s data protection authority",
    onClick: () => window.open("https://www.cnil.fr/en/home", "_blank", "noopener,noreferrer"),
    leftSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "profile-purpose",
    label: "User Profile Data – Legal Basis",
    description:
      "GitHub user profile data (public avatar, username) is processed under legitimate interest, and displayed for community purposes (e.g., leaderboard).",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "profile-legal-framework",
    label: "Profile Usage & Objectives",
    description:
      "The use of GitHub profiles enables a functional user experience for registration and ranking within public dashboards.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "profile-objectives",
    label: "Profile Retention",
    description:
      "We store profile data as long as your account is active. Post-deletion, we retain limited data for legal compliance as necessary.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "profile-duration",
    label: "Profile Data Storage",
    description:
      "Data is hosted on servers within the EU (France – Supabase region Paris). No transfers outside the EU are currently made.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "profile-transfer",
    label: "Profile Recipients",
    description: "Profile information may be shared internally or with legal authorities if required by law.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "profile-recipients",
    label: "Your Profile Rights",
    description:
      "You have full rights under GDPR regarding your profile data: access, correction, deletion, objection, portability, and post-mortem directives.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "profile-rights",
    label: "Your Rights Regarding Profile Data",
    description: "You maintain full GDPR rights over your profile data, including after your death.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },

  {
    id: "profile-cnil",
    label: "Profile Data – CNIL Complaint",
    description: "If you believe your profile data is mishandled, you can contact CNIL to file a complaint.",
    onClick: () => window.open("https://www.cnil.fr/en/home", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "visit-logs",
    label: "Visit: Data Collection",
    description:
      "Navigation and connection metadata are collected automatically for site operation and security monitoring (e.g., IP address).",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "visit-purpose",
    label: "Visit Purpose",
    description:
      "This data is used to detect fraud, manage abuse, and secure the platform (with help from IPQualityScore and Cloudflare).",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "visit-retention",
    label: "Visit Data Retention",
    description: "Navigation logs are kept for 14 months unless longer retention is required by law.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "visit-transfer",
    label: "Visit Transfers",
    description: "Visit data is only shared when legally required or with your explicit consent.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "visit-recipients",
    label: "Visit Recipients",
    description: "Authorized staff and third-party security providers may access visit logs as needed.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "visit-rights",
    label: "Your Visit Rights",
    description: "You have rights over this data just like any other personal information under GDPR.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "visit-cnil",
    label: "CNIL Complaints – Visits",
    description: "You may submit complaints about visit tracking practices to CNIL via their official site.",
    onClick: () => window.open("https://www.cnil.fr/en/home", "_blank", "noopener,noreferrer"),
    rightSection: <IconShield aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "cookies-usage",
    label: "Use of Cookies",
    description:
      "a11yStart uses only strictly necessary cookies for functionality. No tracking or ad cookies are used.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconCookie aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "google-analytics",
    label: "Google Analytics",
    description:
      "We use Google Analytics to analyze website traffic anonymously. We anonymize IP addresses before they are processed by Google.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "ip-anonymization",
    label: "IP Anonymization",
    description: "Your IP address is anonymized within the EU before being sent to Google, except in rare cases.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },

  {
    id: "browser-plugin",
    label: "Opt-out via Browser",
    description: "You can opt out of cookie storage by adjusting your browser settings. More info here.",
    onClick: () => window.open("https://www.internetcookies.com/", "_blank", "noopener,noreferrer"),
    rightSection: <IconCookie aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "privacy-google",
    label: "Google’s Policy",
    description: "For details on how Google handles your data: Google Privacy & Terms",
    onClick: () => window.open("https://policies.google.com/privacy/frameworks", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "cloudflare",
    label: "Cloudflare Protection",
    description:
      "We use Cloudflare to mitigate DDoS attacks and secure our infrastructure. Privacy Policy: Cloudflare Privacy",
    onClick: () => window.open("https://www.cloudflare.com/privacypolicy", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "data-security",
    label: "Data Security Measures",
    description:
      "We apply security headers, Supabase's encrypted data layer, role-based access, Cloudflare WAF, and IPQualityScore to protect your data.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "external-sites",
    label: "External Services & Partners",
    description:
      "We integrate with: GitHub (auth), Stripe (payments), Supabase (backend), Mailo (email hosting), EmailJS (form-to-email), IPQualityScore (fraud detection), SightEngine (moderation)",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "contact",
    label: "Contact & Rights Requests",
    description: "You can exercise your data rights by contacting us at:",
    onClick: () => window.open("mailto:contact@APPLICATION.domain", "_blank"),
    rightSection: <IconMail aria-label="Opens your default email application" size={24} stroke={1.5} />,
  },
  {
    id: "policy-updates",
    label: "Privacy Policy Updates",
    description:
      "This policy may be updated without prior notice. You will be informed by email or via an announcement on our site.",
    onClick: () => onSubmit("/privacy-policy"),
    leftSection: <IconShield aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "partner-cloudflare",
    label: "Partner: Cloudflare",
    description: "GDPR policy by Cloudflare (🇫🇷 French)",
    onClick: () => window.open("https://www.cloudflare.com/fr-fr/trust-hub/gdpr/", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "partner-mailo",
    label: "Partner: Mailo",
    description: "Confidentiality rules by Mailo (🇫🇷 French)",
    onClick: () =>
      window.open("https://www.mailo.com/mailo/fr/regles-de-confidentialite.php", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Navigate to the selected page" size={24} stroke={1.5} />,
  },
  {
    id: "partner-ipqs",
    label: "Partner: IPQualityScore",
    description: "Real-time fraud prevention platform (🇬🇧 English)",
    onClick: () => window.open("https://www.ipqualityscore.com/privacy-policy", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "partner-sightengine",
    label: "Partner: SightEngine",
    description: "Content moderation GDPR policy (🇬🇧 English)",
    onClick: () => window.open("https://sightengine.com/faq/gdpr-content-moderation", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "partner-emailjs",
    label: "Partner: EmailJS",
    description: "Client-side email service privacy policy (🇬🇧 English)",
    onClick: () =>
      window.open("https://www.emailjs.com/legal/data-protection-agreement/", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "partner-stripe",
    label: "Partner: Stripe",
    description: "Payment processor privacy policy (🇫🇷 French)",
    onClick: () => window.open("https://stripe.com/fr/privacy", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
  {
    id: "partner-supabase",
    label: "Partner: Supabase",
    description: "PostgreSQL backend service GDPR policy (🇬🇧 English)",
    onClick: () => window.open("https://supabase.com/privacy", "_blank", "noopener,noreferrer"),
    rightSection: <IconExternalLink aria-label="Opens an external website in a new tab" size={24} stroke={1.5} />,
  },
];
